import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ApiInspector } from '@/components/api-inspector/api-inspector';
import { BootErrorScreen, BootLoadingScreen, ConfigurationErrorScreen } from '@/components/layout/boot-screen';
import { ScrollToTop } from '@/components/layout/page';
import { Spinner } from '@/components/ui/primitives';
import { appConfig, configErrors } from '@/config/env';
import { useSession } from '@/hooks/use-session';
import { useSiteContext } from '@/hooks/use-site';
import { paths } from '@/lib/paths';
import AccountPage from '@/pages/account';
import CancelSubscriptionPage from '@/pages/cancel-subscription';
import CheckoutPage from '@/pages/checkout';
import CheckoutSuccessPage from '@/pages/checkout-success';
import CompanyPage from '@/pages/company';
import ContactPage from '@/pages/contact';
import ForgotPasswordPage from '@/pages/forgot-password';
import HomePage from '@/pages/home';

import { LegalNoticePage, PrivacyPage, TermsPage } from '@/pages/legal';
import LoginPage from '@/pages/login';
import NewsletterPage from '@/pages/newsletter';
import NotFoundPage from '@/pages/not-found';
import PricingPage from '@/pages/pricing';
import ResetPasswordPage from '@/pages/reset-password';
import SearchPage from '@/pages/search';
import UnsubscribePage from '@/pages/unsubscribe';

import ServicesPage from '@/pages/services';
import EntreprisePage from '@/pages/entreprise';

import { ProtectedRoute } from '@/components/layout/protected-route';

// Importez vos composants de pages correspondants
import ResultatPage from '@/pages/resultat'; 
import DownloadPage from '@/pages/download';

/**
 * Racine de l'application : garde de démarrage, table des routes, inspecteur.
 *
 * L'ordre compte. Rien n'est monté tant que la configuration du site n'est pas
 * connue : les pages peuvent alors lire `useSite()` sans jamais tester la
 * présence de la donnée, ce qui élimine des dizaines de vérifications
 * défensives dans le reste du code.
 */
export default function App() {
    const { site, loading, error, reload } = useSiteContext();

    // 1. Les variables d'environnement sont-elles exploitables ?
    if (configErrors.length > 0) {
        return <ConfigurationErrorScreen />;
    }

    // 2. GET /site est-il arrivé ?
    if (loading && !site) {
        return <BootLoadingScreen />;
    }

    // 3. A-t-il échoué ?
    if (!site) {
        return (
            <BootErrorScreen
                error={
                    error ?? {
                        message: 'La configuration du site est indisponible.',
                        retryable: true,
                    }
                }
                onRetry={reload}
            />
        );
    }

    return (
        <>
            <ScrollToTop />

            <Routes>
                <Route path={paths.home} element={<HomePage />} />
                <Route path={paths.search} element={<SearchPage />} />
                <Route path={paths.companyPattern} element={<CompanyPage />} />
                <Route path={paths.pricing} element={<PricingPage />} />

                <Route path={paths.checkout} element={<CheckoutPage />} />
                <Route path={paths.checkoutSuccess} element={<CheckoutSuccessPage />} />

                <Route path={paths.login} element={<LoginPage />} />
                <Route path={paths.forgotPassword} element={<ForgotPasswordPage />} />
                <Route path={paths.resetPassword} element={<ResetPasswordPage />} />

                <Route path={paths.services} element={<ServicesPage />} />
                <Route path={paths.entreprise} element={<EntreprisePage />} />

                <Route
                    path={paths.account}
                    element={
                        <RequireAuth>
                            <AccountPage />
                        </RequireAuth>
                    }
                />

                <Route path={paths.cancelSubscription} element={<CancelSubscriptionPage />} />
                <Route path={paths.newsletter} element={<NewsletterPage />} />
                <Route path={paths.unsubscribe} element={<UnsubscribePage />} />
                <Route path={paths.contact} element={<ContactPage />} />


                <Route path={paths.legal} element={<LegalNoticePage />} />
                <Route path={paths.terms} element={<TermsPage />} />
                <Route path={paths.privacy} element={<PrivacyPage />} />

                {/* Alias courants : le robot de vérification visite /cgv et
                    /cgu, et certains visiteurs tapent /pricing. */}
                <Route path="/cgv" element={<Navigate to={paths.terms} replace />} />
                <Route path="/cgu" element={<Navigate to={paths.terms} replace />} />
                <Route path="/pricing" element={<Navigate to={paths.pricing} replace />} />

                <Route path="*" element={<NotFoundPage />} />

            {/* --- ROUTES PROTÉGÉES (Middleware 'auth') --- */}
            {/* Toutes les routes imbriquées ici exigeront 'hasAccess === true' */}
            <Route element={<ProtectedRoute />}>
                {/* Équivalent de Route::get('/', 'index') sous le préfixe /resultat */}
                <Route path="/resultat" element={<ResultatPage />} />
                
                {/* Équivalent de Route::get('/download', 'downloadFiche') */}
                <Route path="/resultat/download" element={<DownloadPage />} />
            </Route>


            </Routes>

            {appConfig.showApiInspector && <ApiInspector />}
        </>
    );
}

/**
 * Garde de route : réserve un écran aux visiteurs connectés.
 *
 * Deux subtilités :
 *
 * - on attend `ready`, c'est-à-dire la vérification du token trouvé dans le
 *   stockage local. Sans cette attente, un rafraîchissement sur `/compte`
 *   renverrait brièvement vers la connexion avant de revenir — un
 *   clignotement disgracieux et déroutant ;
 * - l'adresse demandée est mémorisée dans `location.state.from`, pour y
 *   revenir après la connexion.
 *
 * Cette garde n'est **pas** une protection : elle améliore le parcours. La
 * véritable protection est côté API, qui refuse tout appel sans token valide.
 */
function RequireAuth({ children }: { children: ReactNode }) {
    const { isAuthenticated, ready } = useSession();
    const location = useLocation();

    if (!ready) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Spinner label="Vérification de votre session…" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to={paths.login} replace state={{ from: location.pathname + location.search }} />;
    }

    return <>{children}</>;
}
